import { useState, useEffect } from "react";
import { useModalStore } from "../../../stores/modalStore.js";

const DEFAULTS = {
  enabled:      true,
  blockedWords: [],
  spamThreshold: 5,
  mentionLimit:  10,
  linkFilter:    false,
  capsFilter:    false,
  actions: { onSpam: 'delete', onBadWord: 'delete', onMention: 'delete' },
  logChannelId: null,
};

const ACTION_OPTS = ['delete', 'timeout', 'kick'];

export function AutoModModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const serverId = data?.serverId;

  const [cfg,       setCfg]       = useState(DEFAULTS);
  const [wordInput, setWordInput] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    if (!isOpen || type !== 'automod' || !serverId) return;
    setLoading(true);
    fetch(`/api/servers/${serverId}/automod`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setCfg({ ...DEFAULTS, ...d.config }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, type, serverId]);

  if (!isOpen || type !== 'automod') return null;

  const update = (patch) => setCfg(c => ({ ...c, ...patch }));
  const updateAction = (key, val) => setCfg(c => ({ ...c, actions: { ...c.actions, [key]: val } }));

  const addWord = () => {
    const w = wordInput.trim().toLowerCase();
    if (!w || cfg.blockedWords.includes(w)) return;
    update({ blockedWords: [...cfg.blockedWords, w] });
    setWordInput('');
  };

  const removeWord = (w) => update({ blockedWords: cfg.blockedWords.filter(x => x !== w) });

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`/api/servers/${serverId}/automod`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify(cfg),
      });
      localStorage.setItem('automod_blocked_words', JSON.stringify(cfg.blockedWords));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 540, maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🛡️ AutoMod Rules</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Automatically moderate messages in your server</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Master toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Enable AutoMod</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Apply all rules below to incoming messages</div>
              </div>
              <Toggle checked={cfg.enabled} onChange={v => update({ enabled: v })} />
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)' }} />

            {/* Blocked words */}
            <section>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Blocked Words</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input value={wordInput} onChange={e => setWordInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addWord()}
                  placeholder="Add a word or phrase…"
                  style={{ flex: 1, padding: '7px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none' }} />
                <button onClick={addWord} style={{ padding: '7px 16px', borderRadius: 8, background: 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add</button>
              </div>
              {cfg.blockedWords.length === 0 ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>No blocked words yet</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {cfg.blockedWords.map(w => (
                    <span key={w} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', fontSize: 12, color: '#fca5a5' }}>
                      {w}
                      <button onClick={() => removeWord(w)} style={{ background: 'none', border: 'none', color: 'rgba(239,68,68,0.7)', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}>✕</button>
                    </span>
                  ))}
                </div>
              )}
            </section>

            {/* Filters */}
            <section>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Filters</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <ToggleRow label="Link Filter" sub="Block all links" checked={cfg.linkFilter} onChange={v => update({ linkFilter: v })} />
                <ToggleRow label="Caps Filter" sub="Block messages that are >70% CAPS" checked={cfg.capsFilter} onChange={v => update({ capsFilter: v })} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>Spam Threshold</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Max messages per 5 seconds before action</div>
                  </div>
                  <input type="number" min={2} max={20} value={cfg.spamThreshold}
                    onChange={e => update({ spamThreshold: parseInt(e.target.value) || 5 })}
                    style={{ width: 64, padding: '5px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, textAlign: 'center', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>Mention Limit</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Max @mentions per message</div>
                  </div>
                  <input type="number" min={1} max={50} value={cfg.mentionLimit}
                    onChange={e => update({ mentionLimit: parseInt(e.target.value) || 10 })}
                    style={{ width: 64, padding: '5px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, textAlign: 'center', outline: 'none' }} />
                </div>
              </div>
            </section>

            {/* Actions */}
            <section>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { key: 'onBadWord', label: 'On blocked word' },
                  { key: 'onSpam',    label: 'On spam' },
                  { key: 'onMention', label: 'On mention spam' },
                ].map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{label}</span>
                    <select value={cfg.actions[key]} onChange={e => updateAction(key, e.target.value)}
                      style={{ padding: '5px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
                      {ACTION_OPTS.map(a => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 20px', borderRadius: 8, background: saved ? '#22c55e' : 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}>
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save Rules'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', transition: 'background 0.2s', position: 'relative',
        background: checked ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.15)' }}>
      <span style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: checked ? 23 : 3 }} />
    </button>
  );
}

function ToggleRow({ label, sub, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{sub}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}
